import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { ArrowLeft, Receipt, AlertTriangle, Package, Trash2, Pencil, Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { makeApi, authedUrl } from "@/lib/api";
import { money, dateShort, STATUS_STYLES } from "@/lib/format";
import DocViewer from "@/components/DocViewer";
import { DocItem } from "@/components/DocItem";
import { DeleteDocDialog } from "@/components/DeleteDocDialog";
import { DriverSelect } from "@/components/loads/DriverSelect";
import { DeleteLoadDialog } from "@/components/loads/DeleteLoadDialog";
import { EditLoadDialog } from "@/components/loads/EditLoadDialog";
import { Accessorials } from "@/components/loads/Accessorials";
import { ApptLine } from "@/components/loads/ApptLine";
import { AdminDocUpload } from "@/components/loads/AdminDocUpload";

export default function LoadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [load, setLoad] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [viewerItem, setViewerItem] = useState(null);
  const [deletingDoc, setDeletingDoc] = useState(null);
  const [deletingLoad, setDeletingLoad] = useState(false);
  const [editing, setEditing] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [reading, setReading] = useState(false);

  const readRatecon = async (doc) => {
    setReading(true);
    const t = toast.loading("Reading ratecon…");
    try {
      const { data } = await makeApi(getToken).post(`/documents/${doc.id}/extract`, null, { timeout: 120000 });
      toast.dismiss(t);
      setPrefill(data);
      setEditing(true);
    } catch (e) {
      toast.dismiss(t);
      toast.error(e?.response?.data?.detail || "Couldn't read this ratecon. You can still edit the load by hand.");
    } finally {
      setReading(false);
    }
  };

  const refresh = async () => {
    const api = makeApi(getToken);
    try {
      const [l, d] = await Promise.all([api.get(`/loads/${id}`), api.get("/drivers")]);
      setLoad(l.data);
      setDrivers(d.data);
    } catch (e) {
      if (e?.response?.status === 404) {
        toast.error("This load is not available (it may be in Trash)");
        navigate("/admin");
      } else toast.error("Could not load this load");
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line
  }, [id]);

  if (!load) return <div className="text-slate-500">Loading…</div>;

  const ratecons = load.documents?.filter((d) => d.type === "Ratecon") || [];
  const pods = load.documents?.filter((d) => d.type === "POD") || [];
  const invoice = load.invoice;
  const openDoc = (d) => setViewerItem({ kind: "document", id: d.id, filename: d.original_filename, content_type: d.content_type });

  const changeStatus = async (status) => {
    const api = makeApi(getToken);
    await api.patch(`/loads/${id}`, { status });
    toast.success(`Status set to ${status}`);
    refresh();
  };
  const changeDriver = async (driverId) => {
    const api = makeApi(getToken);
    await api.patch(`/loads/${id}`, { assigned_driver_id: driverId });
    toast.success("Driver updated");
    refresh();
  };

  const generateInvoice = async () => {
    const api = makeApi(getToken);
    try {
      const { data } = await api.post(`/loads/${id}/invoice`);
      toast.success(`Invoice ${data.invoice_number} created`);
      navigate(`/admin/invoices/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create invoice");
    }
  };

  const downloadPacket = async () => {
    window.open(await authedUrl(`/loads/${id}/packet`, getToken), "_blank");
  };

  return (
    <div className="space-y-6" data-testid="load-detail-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
          <ArrowLeft className="w-4 h-4" /> All loads
        </Link>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={() => { setPrefill(null); setEditing(true); }} data-testid="edit-load-btn">
            <Pencil className="w-4 h-4 mr-1" /> Edit load
          </Button>
          <Button variant="outline" onClick={() => setDeletingLoad(true)} className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid="delete-load-btn">
            <Trash2 className="w-4 h-4 mr-1" /> Delete load
          </Button>
          <Button variant="outline" onClick={downloadPacket} data-testid="download-packet-btn">
            <Package className="w-4 h-4 mr-1" /> Export packet
          </Button>
          {!invoice ? (
            <Button onClick={generateInvoice} className="bg-[#002FA7] hover:bg-[#001A75] rounded-full" data-testid="generate-invoice-btn">
              <Receipt className="w-4 h-4 mr-1" /> Generate invoice
            </Button>
          ) : (
            <Link to={`/admin/invoices/${invoice.id}`}>
              <Button className="bg-[#002FA7] hover:bg-[#001A75] rounded-full" data-testid="view-invoice-btn">
                <Receipt className="w-4 h-4 mr-1" /> View invoice{invoice.invoice_number && <span className="ml-1.5 font-mono" data-testid="load-invoice-number">{invoice.invoice_number}</span>}
              </Button>
            </Link>
          )}
        </div>
      </div>

      <Card className="p-6 border-slate-200">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs text-slate-500">LOAD #</div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }} data-testid="load-carrier-number">{load.carrier_number || "—"}</h1>
            <div className="mt-1 inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-600" data-testid="load-broker-ref">
              <span className="uppercase tracking-wider text-slate-500">Broker ref #</span><span className="font-mono font-medium text-slate-800">{load.load_number}</span>
            </div>
            <div className="mt-2 text-sm text-slate-600" data-testid="load-broker">{load.broker_name}</div>
            <div className="mt-2 space-y-1 text-sm text-slate-700">
              <div data-testid="load-pickup-full"><span className="text-xs text-slate-500 mr-2">PICKUP</span>{load.pickup_location}</div>
              <div data-testid="load-delivery-full"><span className="text-xs text-slate-500 mr-2">DELIVERY</span>{load.delivery_location}</div>
            </div>
            <div className="mt-2 text-xs text-slate-500">Created {dateShort(load.created_at)}</div>
            <div className="mt-2 space-y-1" data-testid="load-appointments">
              <div className="text-xs text-slate-500">Pickup <ApptLine date={load.pickup_date} appt={load.pickup_appt} testId="load-pickup-appt" className="ml-1" /></div>
              <div className="text-xs text-slate-500">Delivery <ApptLine date={load.delivery_date} appt={load.delivery_appt} testId="load-delivery-appt" className="ml-1" /></div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500">RATE</div>
            <div className="text-3xl font-bold text-[#002FA7]" style={{ fontFamily: "Outfit" }}>{money(load.rate)}</div>
            {load.accessorials_total > 0 && (
              <div className="mt-1 text-sm text-slate-600" data-testid="header-billable-total">+ {money(load.accessorials_total)} accessorials = <span className="font-semibold text-slate-900">{money(load.billable_total)}</span></div>
            )}
          </div>
        </div>

        <div className="mt-6 grid md:grid-cols-3 gap-4">
          <div>
            <div className="text-xs text-slate-500 mb-1">Status</div>
            <Select value={load.status} onValueChange={changeStatus}>
              <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["New", "In Progress", "Delivered", "Invoiced"].map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="mt-2">
              <span className={`pill ${STATUS_STYLES[load.status]}`}>{load.status}</span>
            </div>
          </div>
          <div>
            <div className="text-xs text-slate-500 mb-1">Driver</div>
            <DriverSelect load={load} drivers={drivers} onChange={changeDriver} testId="assign-driver-select" />
          </div>
          <div>
            <div className="text-xs text-slate-500 mb-1">Flags</div>
            <div className="flex flex-wrap gap-2 mt-1">
              {load.status === "Delivered" && !load.has_pod && (
                <span className="pill bg-amber-50 text-amber-800 border border-amber-200">
                  <AlertTriangle className="w-3 h-3" /> Missing POD
                </span>
              )}
              {load.status === "Delivered" && !invoice && (
                <span className="pill bg-rose-50 text-rose-800 border border-rose-200">
                  <Receipt className="w-3 h-3" /> Unbilled
                </span>
              )}
              {load.has_pod && (
                <span className="pill bg-emerald-50 text-emerald-700 border border-emerald-200">POD on file</span>
              )}
              {load.lumpers_to_bill > 0 && (
                <span className="pill bg-amber-50 text-amber-800 border border-amber-200" data-testid="flag-lumper-billback">
                  <Receipt className="w-3 h-3" /> Lumper to bill back
                </span>
              )}
            </div>
          </div>
        </div>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="p-6 border-slate-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold" style={{ fontFamily: "Outfit" }}>Ratecon{ratecons.length > 1 ? `s (${ratecons.length})` : ""}</h2>
            {ratecons.length === 0 && <span className="text-xs text-slate-400">Not uploaded</span>}
            {ratecons.length > 0 && (
              <Button size="sm" variant="outline" onClick={() => readRatecon(ratecons[0])} disabled={reading} className="text-[#002FA7] border-blue-200 hover:bg-blue-50" data-testid="autofill-from-ratecon-btn">
                {reading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
                {reading ? "Reading ratecon…" : "Auto-fill from ratecon"}
              </Button>
            )}
          </div>
          <div className="mt-4 space-y-2" data-testid="ratecon-list">
            {ratecons.map((d) => <DocItem key={d.id} doc={d} onOpen={openDoc} onDelete={setDeletingDoc} />)}
          </div>
          <AdminDocUpload loadId={id} type="Ratecon" loadNumber={load.carrier_number || load.load_number} onDone={(docs) => { refresh(); readRatecon(docs[0]); }} compact={ratecons.length > 0} />
        </Card>

        <Card className="p-6 border-slate-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold" style={{ fontFamily: "Outfit" }}>PODs / BOLs ({pods.length})</h2>
          </div>
          {pods.length === 0 ? (
            <p className="mt-4 text-sm text-slate-500" data-testid="pod-empty">None yet. The driver can upload from their phone, or you can add one here — the load is marked Delivered.</p>
          ) : (
            <div className="mt-4 space-y-2" data-testid="pod-list">
              {pods.map((d) => <DocItem key={d.id} doc={d} onOpen={openDoc} onDelete={setDeletingDoc} />)}
            </div>
          )}
          <AdminDocUpload loadId={id} type="POD" loadNumber={load.carrier_number || load.load_number} onDone={refresh} compact={pods.length > 0} />
        </Card>
      </div>

      <Accessorials load={load} locked={invoice?.status === "Sent"} onChanged={refresh} onOpenDoc={openDoc} onDeleteDoc={setDeletingDoc} />

      <DocViewer open={!!viewerItem} onClose={() => setViewerItem(null)} item={viewerItem} />
      <DeleteDocDialog doc={deletingDoc} onClose={() => setDeletingDoc(null)} onDeleted={refresh} />
      <DeleteLoadDialog
        load={deletingLoad ? load : null}
        onClose={() => setDeletingLoad(false)}
        onDeleted={() => navigate("/admin")}
      />
      <EditLoadDialog open={editing} load={load} drivers={drivers} prefill={prefill} onClose={() => { setEditing(false); setPrefill(null); }} onSaved={refresh} />
    </div>
  );
}
